import * as vp from 'vopay';
import { FunctionComponent, JSX, useState } from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavMenu } from '../../../../providers/NavMenu.provider';
import styles from '../Transactions.module.scss';
import useFileDownload from '../../../../hooks/useFiledDownload';

const { Table, Box, SubTitle, Badge, Button, DropDown, formattedCurrency, Description } = vp;

type DataProps = {
  transactionId: string;
  activeModule: string;
  hasNewTransactionButton: boolean;
  clientAccountId: string;
};

type TransactionInfoProps = {
  transaction: vp.BaseTransactionResponse;
  autoBalanceTransferReport: vp.AutoBalanceTransferReportResponse;
  transactionHeader: vp.TransactionsLists;
};

type FC = FunctionComponent<TransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetail: () => vp.TableProps;
  accountHolderDetails: () => vp.TableProps;
  bankAccountDetails: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
  autoBalanceReport: () => vp.TableProps;
};

const AutoBalanceTransferTransactionInfo: FC = ({
  transaction,
  transactionHeader,
  autoBalanceTransferReport,
}: TransactionInfoProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.AutoBalanceTransferTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const history = useHistory();
  const { onSubNavClick } = useNavMenu();

  const { state } = useLocation<DataProps>();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const {
    downloadPdfTable,
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
  } = useFileDownload();
  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table {...(getTableProps('transactionDetail') || '')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('accountHolderDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('accountHolderDetails')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('bankAccountDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('bankAccountDetails')} />
      {transaction.FailureReason && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoTitle') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}

      <Box mt="lg" mb="md" jc="sb">
        <SubTitle subTitle={t('autoBalanceTransactionBreakdown') || ''} />
        {autoBalanceTransferReport.Success && (
          <div className={styles.dropdownContainer}>
            <Button
              id="export-transactions"
              color="none"
              label={t('exportDd.export')}
              height="38px"
              textSize={1}
              background="transparent"
              noShadow
              icon="arrowDown"
              iconLocation="right"
              onClick={() => {
                setIsMenuVisible(true);
              }}
            />
            {isMenuVisible && (
              <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
                <Button
                  id="export-PDF-btn"
                  label={t('exportDd.pdf')}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadPdfTable('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-CSV-btn"
                  label={t('exportDd.csv')}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadCsvFile('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-XLS-btn"
                  label={t('exportDd.xls')}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadXls('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-DOC-btn"
                  label={t('exportDd.doc')}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadDoc('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-Text-btn"
                  label={t('exportDd.text')}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadTxt('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-JSON-btn"
                  label={t('exportDd.json')}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadJson(
                      Object.values(autoBalanceTransferReport.Transactions).map((reportTransaction) => ({
                        id: reportTransaction.TransactionID,
                        name: reportTransaction.FullName,
                        transactionDate: reportTransaction.TransactionDate,
                        type: tGlobal(
                          `Transaction.TransactionHistory.transactionTypes.${reportTransaction.TransactionType}`,
                          { defaultValue: reportTransaction.TransactionType },
                        ),
                        referenceNumber: reportTransaction.ReferenceNo,
                        amount: `$${
                          reportTransaction.CreditAmount === '0.00'
                            ? formattedCurrency(reportTransaction.DebitAmount)
                            : formattedCurrency(reportTransaction.CreditAmount)
                        }`,
                      })),
                      'auto-balance-transfer-transactions',
                    );
                  }}
                />
                <Button
                  id="export-XML-btn"
                  label={t('exportDd.xml')}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    downloadXml('auto-balance-transactions-table', 'auto-balance-transfer-transactions');
                  }}
                />
                <Button
                  id="export-Print-btn"
                  label={t('exportDd.print')}
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    printFile('auto-balance-transactions-table');
                  }}
                />
              </DropDown>
            )}
          </div>
        )}
      </Box>
      {autoBalanceTransferReport.Success && <Table {...getTableProps('autoBalanceReport')} />}
      {!autoBalanceTransferReport.Success && <Description>{t('transactionsNotRetrieved')}</Description>}
    </>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetail: () => {
        const transactionDetailsPairs: KeyValuePair[] = [
          {
            display: t('transactionDetail.transactionID'),
            value: transaction.TransactionID,
          },
          {
            display: t('transactionDetail.transactionStatus'),
            value: (
              <Badge
                label={vp.capitalizeFirstLetter(transaction.TransactionStatus)}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
          },
          {
            display: t('transactionDetail.transactionDate'),
            value: transaction.TransactionDateTime,
          },
          {
            display: t('transactionDetail.lastModified'),
            value: transaction.LastModified,
          },
          {
            display: t('transactionDetail.holdReleaseDate'),
            value: transaction.HoldReleaseDateTime,
          },
          {
            display: t('transactionDetail.type'),
            value:
              tGlobal('Transaction.TransactionHistory.transactionTypes.Auto Balance Transfer', {
                defaultValue: 'Auto Balance Transfer',
              }) || '',
          },
          {
            display: t('transactionDetail.amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            display: t('transactionDetail.holdAmount'),
            value: `$${formattedCurrency(transaction.HoldAmount)}`,
          },
          {
            display: t('transactionDetail.currency'),
            value: transaction.Currency,
          },
          {
            display: t('transactionDetail.notes'),
            value: transactionHeader.Notes,
          },
        ];

        const transactionDetailsProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetail.header') || '' },
            detailValue: { header: '' },
          },
          rows: transactionDetailsPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return transactionDetailsProps;
      },
      accountHolderDetails: () => {
        const accountHolderDetailsPairs: KeyValuePair[] = [
          {
            display: t('accountHolderDetails.name'),
            value: (
              <Box jc="fs" ai="center" gap="sm">
                <Box>{`${transaction.FirstName} ${transaction.LastName}`}</Box>
                {state && state.clientAccountId && state.clientAccountId !== '' && (
                  <Button
                    id="view-client-account-details"
                    label={t('accountHolderDetails.view')}
                    width="50px"
                    height="30px"
                    color="secondary"
                    onClick={() => {
                      history.push(`/client-accounts/${state.clientAccountId}`);
                      onSubNavClick(
                        'clientAccountsList',
                        `/client-accounts/${state.clientAccountId}`,
                        t('accountHolderDetails.clientAccountsList'),
                      );
                    }}
                  />
                )}
              </Box>
            ),
            visible: !!transaction.FirstName,
          },
          {
            display: t('accountHolderDetails.companyName'),
            value: transaction.CompanyName,
            visible: !!transaction.CompanyName,
          },
          {
            display: t('accountHolderDetails.address'),
            value: `${transaction.Address1} ${transaction.Address2}`,
            visible: transaction.Address1 !== '',
          },
          {
            display: t('accountHolderDetails.city'),
            value: transaction.City,
            visible: transaction.City !== '',
          },
          {
            display: t('accountHolderDetails.postalCode'),
            value: transaction.PostalCode || transaction['PostalCode/Zipcode'] || '',
            visible: !!transaction.PostalCode || !!transaction['PostalCode/Zipcode'],
          },
          {
            display: t('accountHolderDetails.province'),
            value: transaction.Province || transaction['Province/State'] || '',
            visible: !!transaction['Province/State'] || !!transaction.Province,
          },
          {
            display: t('accountHolderDetails.country'),
            value: transaction.Country,
            visible: true,
          },
          {
            display: t('accountHolderDetails.primaryContact'),
            value: transaction.PhoneNumber,
            visible: transaction.PhoneNumber !== '',
          },
        ];

        const accountHolderDetailsProps: vp.TableProps = {
          name: 'account-holder-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('accountHolderDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountHolderDetailsPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountHolderDetailsProps;
      },
      bankAccountDetails: () => {
        const bankingInformationPairs: KeyValuePair[] = [
          {
            display: t('bankAccountDetails.financialInstitutionNumber'),
            value: transaction.FinancialInstitutionNumber,
          },
          {
            display: t('bankAccountDetails.branchTransitNumber'),
            value: transaction.BranchTransitNumber,
          },
          {
            display: t('bankAccountDetails.accountNumber'),
            value: transaction.AccountNumber,
          },
        ];

        const bankingInformationProps: vp.TableProps = {
          name: 'banking-information',
          variant: 'pair',
          columns: {
            detailKey: { header: t('bankAccountDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: bankingInformationPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return bankingInformationProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('errorInfo.failureDateTime'),
            value: transaction.LastModified,
          },
          {
            display: t('errorInfo.amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            display: t('errorInfo.failureReason'),
            value: transaction.FailureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInfo.header') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
      autoBalanceReport: () => ({
        id: 'auto-balance-transactions-table',
        name: 'transactions',
        sortable: true,
        columns: {
          id: { header: t('autoBalanceReport.id') || '', align: 'left' },
          name: { header: t('autoBalanceReport.name') || '', align: 'left' },
          transactionDate: { header: t('autoBalanceReport.transactionDate') || '', align: 'left' },
          type: { header: t('autoBalanceReport.type') || '', align: 'left' },
          referenceNumber: { header: t('autoBalanceReport.referenceNumber') || '', align: 'left' },
          creditAmount: { header: t('autoBalanceReport.creditAmount') || '', align: 'left' },
          debitAmount: { header: t('autoBalanceReport.debitAmount') || '', align: 'left' },
          details: { header: '', align: 'right', isSortable: false },
        },
        rows: Object.values(autoBalanceTransferReport.Transactions).map((reportTransaction, index) => ({
          id: reportTransaction.TransactionID,
          name: reportTransaction.FullName,
          transactionDate: reportTransaction.TransactionDate,
          type: tGlobal(`Transaction.TransactionHistory.transactionTypes.${reportTransaction.TransactionType}`, {
            defaultValue: reportTransaction.TransactionType,
          }),
          referenceNumber: reportTransaction.ReferenceNo,
          creditAmount: `$${formattedCurrency(reportTransaction.CreditAmount)}`,
          debitAmount: `$${formattedCurrency(reportTransaction.DebitAmount)}`,
          details: (
            <Link
              to={{
                pathname: `/transactions-history/details/${vp.removeWhiteSpaceURI(reportTransaction.TransactionType)}/${
                  reportTransaction.TransactionID
                }/${transactionHeader.Currency}`,
                state: {
                  transactionId: reportTransaction.TransactionID,
                },
              }}
            >
              <Button
                id={`transaction-details-${index}`}
                label={t('autoBalanceReport.details')}
                color="lined"
                noShadow
                height="30px"
                fontUnit="px"
                textSize={14}
              />
            </Link>
          ),
        })),
      }),
    };
    return handlers[handler]();
  }
};

export default AutoBalanceTransferTransactionInfo;
